import { React, useState, useEffect, Drawer, DialogActions, Box, Typography, Divider, IconButton, ArrowBackIosIcon, Default_Text_Color, AlphanumericWithHyphenUnderscoreBlankSpace_Regex } from '../../utils/globalImports';
import { CustomButton, CustomTextField, CustomRadioButton, validationMessages } from '../../utils/globalImports';

interface TagData {
  tagName: string;
  description: string;
  status: string;
}

interface CustomAddTagModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: TagData, setBackendErrors?: (errors: any) => void) => void;
  tagData?: TagData; 
  handleContentChange:(state:boolean)=>void
}

const validationMessageStyle = {
  color: "rgba(255, 0, 0, 0.99)",
  fontSize: "11px",
  paddingLeft: "10px",
  paddingBottom: "10px",
};

const CustomAddTagModal: React.FC<CustomAddTagModalProps> = ({ open, onClose, onSubmit, tagData,handleContentChange }) => {
  const [formData, setFormData] = useState<TagData>({
    tagName: '',
    description: '',
    status: 'Active',
  });

  const [errors, setErrors] = useState({
    TagName: '',
    description: '',
  });

  const [backendErrors, setBackendErrors] = useState<{ [key: string]: string }>({});

  // Validate input fields
  const validate = () => {
    let isValid = true;
    const errors = { TagName: '', description: '' };

    if (!formData.tagName.trim()) {
      errors.TagName = validationMessages.common_required("Tag Name");
      isValid = false;
    } else if (formData.tagName.length > 255) {
      errors.TagName = validationMessages.common_characterLimit("Tag Name", 255);
      isValid = false;
    } else if (!AlphanumericWithHyphenUnderscoreBlankSpace_Regex.test(formData.tagName)) { // Regex to check for alphabets and spaces
      errors.TagName = validationMessages.common_invalidStringAlphanumericWithHyphenUnderscoreBlankSpace("Tag Name");
      isValid = false;
    }

    if(formData.description.length > 1000) {
      errors.description = validationMessages.common_characterLimit("Description", 1000);
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async() => {
    if (validate()) {
      await onSubmit(formData, setBackendErrors);
    }
  };

  // Populate fields if editing a tag
  useEffect(() => {
    if (open) {
      if (tagData) {
        setFormData({
          tagName: tagData.tagName || '',
          description: tagData.description || '',
          status: tagData.status || 'Active',
        });
      } else {
        setFormData({
          tagName: '',
          description: '',
          status: 'Active',
        });
      }
      setErrors({
        TagName: '', description: ''
      });
      setBackendErrors({});
    }
  }, [open, tagData]);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    handleContentChange(true);
  };

  const handleNavigate = () => {
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        width: 350,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 500,
          height: '100%',
          paddingLeft: 2,
          paddingRight: 2,
          paddingTop: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.86)',
        },
      }}
    >
      <Box>
        <Typography
          sx={{
            color: Default_Text_Color,
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
            mt: 2,
            position: 'relative',
            fontWeight: 500,
            fontSize: 22
          }}
        >
          <IconButton
            sx={{
              position: 'absolute',
              left: 0,
              width: '40px',
              height: '40px',
              backgroundColor: 'transparent',
            }}
            onClick={handleNavigate}
          >
            <ArrowBackIosIcon sx={{ color: '#1e66ab', fontSize: '15px' }} />
          </IconButton>
          {tagData ? 'Edit Tag' : 'Add Tag'}
        </Typography>

        <Divider sx={{ mb: 3, mx: "-15px" }}></Divider>

        {/* Client Tag Name */}
        <CustomTextField
          label="Tag Name"
          name="tagName"
          value={formData.tagName}
          onChange={handleChange}
          sx={{ mb: 2, mt:1.5, backgroundColor: '#fff' }}
          required={true}
        />
        {errors.TagName && (
          <Typography style={validationMessageStyle} role="alert">
            {typeof errors.TagName === 'string' ? errors.TagName : JSON.stringify(errors.TagName)}
          </Typography>
        )}  
        {backendErrors.name && <Typography style={validationMessageStyle}>{backendErrors.name}</Typography>}

        {/* Description */}
        <CustomTextField
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          sx={{ mb: 2, mt:1.5, backgroundColor: '#fff',
            '& .MuiInputBase-root': {
              height: 'auto',  // Make sure that the outer container can auto adjust its height
            },
            '& .MuiOutlinedInput-input': {
              minHeight: 100, // Set the minimum height of the input field (works for textareas as well)
              padding: '10px', // Add padding to avoid text sticking to the edges
            }, }}
          required={false}
          isTextArea={true}
        />
        {errors.description && (
          <Typography style={validationMessageStyle} role="alert">
            {typeof errors.description === 'string' ? errors.description : JSON.stringify(errors.description)}
          </Typography>
        )}

        {/* Status */}
        <Typography sx={{ color: '#1e66ab', fontSize: 14, fontWeight: 500, ml: 1, mt: 1 }}>Status</Typography>
        <Box sx={{ display: 'flex' }}>
          <CustomRadioButton
            label="Active"
            value="Active"
            name="status"
            checked={formData.status === 'Active'}
            onChange={handleChange}
            required={true}
          />
          <CustomRadioButton
            label="Inactive"
            value="Inactive"
            name="status"
            checked={formData.status === 'Inactive'}
            onChange={handleChange}
            required={true}
          />
        </Box>

        {/* Submit Button */}
        <DialogActions>
          <CustomButton
            capsule
            sx={{
              textTransform: 'none',
              width: '282px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              mr: 'auto',
              ml: 'auto',
              mt: 2,
            }} 
            onClick={handleSubmit}
          >
            {tagData ? 'Update Tag' : 'Add Tag'}
          </CustomButton>
        </DialogActions>
      </Box>
    </Drawer>
  );
};

export default CustomAddTagModal;